import * as Sentry from '@sentry/react'
import { reportError } from '@/lib/errorReporter'

/**
 * 에러 발생 시 사용자에게 표시할 폴백 UI.
 */
function ErrorFallback() {
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 text-center">
      <h2 className="text-xl font-semibold text-gray-800">문제가 발생했습니다</h2>
      <p className="text-sm text-gray-500">
        일시적인 오류가 발생했습니다. 페이지를 새로고침해 주세요.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
      >
        새로고침
      </button>
    </div>
  )
}

interface SentryErrorBoundaryProps {
  children: React.ReactNode
}

/**
 * Sentry ErrorBoundary 래퍼 컴포넌트.
 *
 * React 렌더링 중 발생하는 에러를 Sentry에 전송하고,
 * 자체 에러 모니터링 시스템에도 전송합니다.
 * 사용자에게 에러 폴백 UI를 표시합니다.
 *
 * SENTRY_DSN이 설정되지 않아도 ErrorBoundary는 동작하며,
 * 에러 캐치 후 폴백 UI만 표시합니다.
 */
export default function SentryErrorBoundary({ children }: SentryErrorBoundaryProps) {
  return (
    <Sentry.ErrorBoundary
      fallback={<ErrorFallback />}
      onError={(error, componentStack) => {
        reportError(error, {
          url: window.location.pathname,
          componentStack: componentStack ?? undefined,
        })
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
